import React from 'react';
import DividerWithText from '../../../../../../components/DividerWithText';
import { Trans, useTranslation } from 'react-i18next';
import { CustomTextField } from '../../../../../../components/CustomTextField';
import { CustomSelect } from '../../../../../../components/CustomSelect';
import { PERIOD_TIME_UNIT_CYCLE } from './Model/Models';
import CustomUserChip from '../../../../../../components/CustomUserChip';
import { IconRightArrow } from '../../../../../../components/buttons/IconButtons';

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * @param item
 * @param onChange
 */
const AttendanceCycle = (
    {
        item,
        onChange,
        emptyFields
    }
) => {

    const { t } = useTranslation();

    const Arrow = () => (
        <span className="mx-1 d-inline-flex align-items-center" aria-hidden="true">
            <IconRightArrow className="w-20px h-20px icon-color-primary-700" />
        </span>
    );

    return (
        <>
            <div className='col-12'>
                <DividerWithText spacing='my-4' title={t('record_configuration')} />
            </div>
            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("check_out_update_tolerance")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("prevent_duplicate_check_out_updates_within_the_specified_time_window")}
                </div>
                <div className="col-12 col-sm-4 pl-sm-0">
                    <CustomTextField
                        id='txt_ac_check_out_cool_down_minutes'
                        type='number'
                        length={2}
                        trans='check_out_cool_down_minutes'
                        name="checkOutCooldown"
                        value={item.checkOutCooldown}
                        onChange={onChange}
                        suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('min')}</p>}
                        inputProps={{
                            min: 1
                        }}
                        required={true}
                        emptyFields={emptyFields}
                        isValid={item.isValidCheckOutCooldown}
                        isValidText={'this_value_must_be_greater_than_0'}
                        helperText={'this_value_must_be_greater_than_0'}
                    />
                </div>
            </div>

            <div className='col-12'>
                <DividerWithText spacing='my-4' title={t('attendance_cycle_configuration')} />
            </div>
            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("cycle_frequency")}</div>
            <div className='col-12'>
                <div className="row">
                    <div className='col-12 col-sm-3 pl-0'>
                        <CustomTextField
                            id='txt_ac_frequency_value'
                            type='number'
                            length={2}
                            trans='frequency_value'
                            name="frequencyValue"
                            placeholder={t('enter_a_number')}
                            value={item.frequencyValue}
                            onChange={onChange}
                            inputProps={{
                                min: 1
                            }}
                            required={true}
                            emptyFields={emptyFields}
                            isValid={item.isValidFrequencyValue}
                            isValidText={'this_value_must_be_greater_than_0'}
                            helperText={'this_value_must_be_greater_than_0'}
                        />
                    </div>
                    <div className='col-12 col-sm-4 pl-0 pl-sm-0'>
                        <CustomSelect
                            id='slt_ac_time_unit'
                            name='frequencyTimeUnit'
                            trans='time_unit'
                            keyName='name'
                            list={PERIOD_TIME_UNIT_CYCLE}
                            value={item.frequencyTimeUnit}
                            onChange={onChange}
                            required={true}
                            emptyFields={emptyFields}
                            showNoSelectionOption={false}
                        />
                    </div>
                </div>
            </div>

            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("deferred_period")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("number_of_deferred_periods_to_review_when_payment_processing_is_delayed")}
                </div>
                <div className="col-12 col-sm-9">
                    <div className='col-12 col-sm-5 pl-0'>
                        <CustomTextField
                            id='txt_ac_number_of_deferred_periods'
                            type='number'
                            length={2}
                            trans='number_of_deferred_periods'
                            name="deferredPeriodValue"
                            value={item.deferredPeriodValue}
                            onChange={onChange}
                            suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('periods')}</p>}
                            inputProps={{
                                min: 1
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="p-3">
                <CustomUserChip
                    className="m-0 chip-primary-200-50 w-100 p-0 justify-content-start"
                    name={
                        <Trans
                            i18nKey="current_period_review_will_start_on_and_close_on"
                            values={{ startPeriod: item?.frequencyStart, endPeriod: item?.frequencyEnd, endTime: item?.frequencyTime }}
                            components={{
                                strong: <span className="fs-12px ibl-font-w-700 font-color-primary-700" />
                            }}
                        />
                    }
                    variant="large"
                />
            </div>

            {/* SUPERVISOR */}
            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("supervisor_review_window")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("time_allocated_for_supervisors_to_review_and_submit_corrections")}
                </div>
                <div className="col-12 col-sm-9">
                    <div className="row">
                        <div className='col-12 col-sm-6 pl-0'>
                            <CustomTextField
                                id='txt_ac_supervisor_review_period_from'
                                type='number'
                                length={2}
                                trans='set_days_before_period_ends_from'
                                name="supervisorReviewPeriodFrom"
                                placeholder={t('enter_a_number')}
                                value={item.supervisorReviewPeriodFrom}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                inputProps={{
                                    min: 1
                                }}
                                required={true}
                                emptyFields={emptyFields}
                                isValid={item.isValidSupervisorReviewPeriodFrom}
                                isValidText={'this_value_must_be_greater_than_0'}
                                helperText={'this_value_must_be_greater_than_0'}
                            />
                        </div>
                        <div className='col-12 col-sm-6 pl-0 pl-sm-3'>
                            <CustomTextField
                                id='txt_ac_supervisor_review_period_to'
                                type='number'
                                length={2}
                                trans='set_days_before_period_ends_to'
                                name="supervisorReviewPeriodTo"
                                placeholder={t('enter_a_number')}
                                value={item.supervisorReviewPeriodTo}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                inputProps={{
                                    min: 1
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ADMIN */}
            <div className="col-12 fs-14px font-color-shades-950 ibl-font-w-600 pb-4">{t("admin_approval_window")}</div>
            <div className="row px-0">
                <div className="col-12 col-sm-3 pb-4 pb-sm-0 fs-12px font-color-shades-950 ibl-font-w-400">
                    {t("time_allocated_for_administrators_to_review_and_approve_corrections")}
                </div>
                <div className="col-12 col-sm-9">
                    <div className="row">
                        <div className='col-12 col-sm-6 pl-0'>
                            <CustomTextField
                                id='txt_ac_admin_approval_period_from'
                                type='number'
                                length={2}
                                trans='days_before_period_starts'
                                name="adminApprovalPeriodFrom"
                                placeholder={t('enter_a_number')}
                                value={item.adminApprovalPeriodFrom}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                inputProps={{
                                    min: 1
                                }}
                                required={true}
                                emptyFields={emptyFields}
                                isValid={item.isValidAdminApprovalPeriodFrom}
                                isValidText={'this_value_must_be_greater_than_0'}
                                helperText={'this_value_must_be_greater_than_0'}
                            />
                        </div>
                        <div className='col-12 col-sm-6 pl-0 pl-sm-3'>
                            <CustomTextField
                                id='txt_ac_admin_approval_period_to'
                                type='number'
                                length={2}
                                trans='set_days_before_period_ends_to'
                                name="adminApprovalPeriodTo"
                                placeholder={t('enter_a_number')}
                                value={item.adminApprovalPeriodTo}
                                onChange={onChange}
                                suffixText={<p className={"ibl-font-w-600 text-uppercase"}>{t('days')}</p>}
                                inputProps={{
                                    min: 1
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>


            <div className="p-3">
                <CustomUserChip
                    variant="large"
                    className="m-0 chip-primary-200-50 w-100 p-0 justify-content-start"
                    name={
                        <div className="d-flex flex-column gap-2">
                            <div className="d-flex flex-wrap align-items-center">
                                <span>{t("supervisor_review")}&nbsp;</span>

                                <span className="py-1 fs-12px ibl-font-w-700 font-color-primary-700">
                                    {item.supervisorStart}
                                </span>

                                <Arrow />

                                <span className="py-1 fs-12px ibl-font-w-700 font-color-primary-700">
                                    {item.supervisorEnd}
                                </span>
                            </div>

                            <div className="d-flex flex-wrap align-items-center">
                                <span>{t("admin_approval")}&nbsp;</span>

                                <span className="py-1 fs-12px ibl-font-w-700 font-color-primary-700">
                                    {item.adminStart}
                                </span>

                                <Arrow />

                                <span className="py-1 fs-12px ibl-font-w-700 font-color-primary-700">
                                    {item.adminEnd}
                                </span>
                            </div>
                        </div>
                    }
                />
            </div>

        </>
    );
}

export default AttendanceCycle
